import { v4 as uuidv4 } from "uuid";
import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import UserRowActionButtons from "./user-row-action-buttons";

// ---------------------------------------------------------------------------

interface UsersTableProps {
  accountList: any[];
  isListClients: boolean;
  parentUserId: string;
}

export interface ITemp {
  randomId: string;
  id: string;
  username: string;
  admin_id?: string;
  user_name?: string;
  display_name?: string;
  credit_reference: number;
  user_status: boolean;
  bet_status: boolean;
  exposure_limit: number;
  default: number;
  account_type: string;
  casino_total: number;
  balance: number;
  client_pnl: number;
  user_exposure: number;
  available_balance: number;
  user_id?: string;
  admin_user_name?: string;
}

type SortDirection = "ascending" | "descending" | "none";
type SortableKey = keyof ITemp;

const ACTIVE_USERS_SORTABLE: SortableKey[] = ["username", "credit_reference"];
const LIST_CLIENTS_SORTABLE: SortableKey[] = ["username", "credit_reference", "balance", "client_pnl", "account_type"];

// ---------------------------------------------------------------------------

const UsersTable: React.FC<UsersTableProps> = ({
  accountList,
  isListClients,
  parentUserId,
}) => {
  const [filteredAccountList, setFilteredAccountList] = useState<ITemp[]>([]);
  const [sortKey, setSortKey] = useState<SortableKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("none");

  const { pathname } = useLocation();
  const isListClientsPath = pathname.split("/")[1] === "list-clients";

  const sortableColumns = isListClients ? LIST_CLIENTS_SORTABLE : ACTIVE_USERS_SORTABLE;

  useEffect(() => {
    if (accountList?.length) {
      const temp: ITemp[] = accountList.map((account: any) => {
        const isUserAccount = String(account.account_type ?? "").toLowerCase() === "user";
        const rowBalance = isUserAccount ? (account.balance ?? 0) : (account.total_balance ?? 0);
        const rowAvailableBalance = isUserAccount
          ? (account.available_balance ?? 0)
          : (account.balance ?? 0);

        return {
          id: account.user_id || account._id || "",
          randomId: uuidv4(),
          user_id: account.user_id,
          username: account.user_name,
          user_name: account.user_name,
          display_name: account.display_name,
          credit_reference: account.credit_reference ?? 0,
          user_status: account.is_active ?? account.status === "Active",
          bet_status: !account.is_bet_lock,
          exposure_limit: account.member_max_exposure_amt ?? 0,
          default: account.default_percentage ?? 0,
          account_type: account.account_type,
          casino_total: account.total_casino ?? 0,
          balance: rowBalance,
          client_pnl: account.client_pnl ?? 0,
          user_exposure: Math.abs(account.user_exposure ?? 0),
          available_balance: rowAvailableBalance,
        };
      });

      setFilteredAccountList(temp);
    } else {
      setFilteredAccountList([]);
    }
    setSortKey(null);
    setSortDirection("none");
  }, [accountList, accountList?.length]);

  const sortedList = useMemo(() => {
    if (!sortKey || sortDirection === "none") return filteredAccountList;

    return [...filteredAccountList].sort((a, b) => {
      const valA = a[sortKey];
      const valB = b[sortKey];

      let comparison = 0;
      if (typeof valA === "string" && typeof valB === "string") {
        comparison = valA.localeCompare(valB, undefined, { sensitivity: "base" });
      } else if (typeof valA === "number" && typeof valB === "number") {
        comparison = valA - valB;
      }

      return sortDirection === "descending" ? -comparison : comparison;
    });
  }, [filteredAccountList, sortKey, sortDirection]);

  const handleSort = (key: SortableKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "ascending" ? "descending" : "ascending"));
    } else {
      setSortKey(key);
      setSortDirection("ascending");
    }
  };

  const getAriaSort = (key: SortableKey): SortDirection => {
    return sortKey === key ? sortDirection : "none";
  };

  const getSrText = (key: SortableKey) => {
    const current = getAriaSort(key);
    if (current === "ascending") return "(Click to sort descending)";
    return "(Click to sort ascending)";
  };

  const totals = {
    credit_reference: filteredAccountList.reduce((sum, a) => sum + a.credit_reference, 0),
    balance: filteredAccountList.reduce((sum, a) => sum + a.balance, 0),
    client_pnl: filteredAccountList.reduce((sum, a) => sum + a.client_pnl, 0),
    user_exposure: filteredAccountList.reduce((sum, a) => sum + a.user_exposure, 0),
    available_balance: filteredAccountList.reduce((sum, a) => sum + a.available_balance, 0),
  };

  const colSpan = isListClients ? 12 : 8;

  const isSortable = (key: SortableKey) => sortableColumns.includes(key);

  const thProps = (key: SortableKey): React.ThHTMLAttributes<HTMLTableHeaderCellElement> => {
    if (!isSortable(key)) return {};
    return {
      tabIndex: 0,
      "aria-sort": getAriaSort(key),
      style: { cursor: "pointer" },
      onClick: () => handleSort(key),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSort(key);
        }
      },
    };
  };

  return (
    <div className="table no-footer list-clients table-responsive-sm">
      <table
        id="eventsListTbl"
        role="table"
        className="table b-table table-striped table-bordered"
      >
        <thead>
          <tr role="row">
            <th role="columnheader" scope="col" className="position-relative" {...thProps("username")}>
              <div>User Name</div>
              {isSortable("username") && <span className="sr-only"> {getSrText("username")}</span>}
            </th>
            <th role="columnheader" scope="col" className="position-relative text-right" {...thProps("credit_reference")}>
              <div>Credit Referance</div>
              {isSortable("credit_reference") && <span className="sr-only"> {getSrText("credit_reference")}</span>}
            </th>
            {isListClients && (
              <>
                <th role="columnheader" scope="col" className="position-relative text-right" {...thProps("balance")}>
                  <div>Balance</div>
                  {isSortable("balance") && <span className="sr-only"> {getSrText("balance")}</span>}
                </th>
                <th role="columnheader" scope="col" className="position-relative text-right" {...thProps("client_pnl")}>
                  <div>Client(P/L)</div>
                  {isSortable("client_pnl") && <span className="sr-only"> {getSrText("client_pnl")}</span>}
                </th>
                <th role="columnheader" scope="col" className="position-relative text-right">
                  <div>Exposure</div>
                </th>
                <th role="columnheader" scope="col" className="position-relative text-right">
                  <div>Available Balance</div>
                </th>
              </>
            )}
            <th role="columnheader" scope="col" className="position-relative">
              <div>U st</div>
            </th>
            <th role="columnheader" scope="col" className="position-relative">
              <div>B st</div>
            </th>
            <th role="columnheader" scope="col" className="position-relative">
              <div>Exposure Limit</div>
            </th>
            <th role="columnheader" scope="col" className="position-relative">
              <div>Deafult (%)</div>
            </th>
            <th role="columnheader" scope="col" className="position-relative" {...thProps("account_type")}>
              <div>Account Type</div>
              {isSortable("account_type") && <span className="sr-only"> {getSrText("account_type")}</span>}
            </th>
            <th role="columnheader" scope="col" className="position-relative">
              <div>Action</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr key="empty-row" role="row">
            <td />
            <td>
              <p className="text-right mb-0 cp">
                {filteredAccountList.length === 0 ? 0 : totals.credit_reference.toLocaleString("en-IN")}
              </p>
            </td>
            {isListClients && (
              <>
                <td>
                  <p className="text-right mb-0">
                    {filteredAccountList.length === 0 ? 0 : totals.balance.toLocaleString("en-IN")}
                  </p>
                </td>
                <td>
                  <p className="text-right mb-0">
                    {filteredAccountList.length === 0 ? 0 : totals.client_pnl.toLocaleString("en-IN")}
                  </p>
                </td>
                <td className="text-right" />
                <td>
                  <p className="text-right mb-0">
                    {filteredAccountList.length === 0 ? 0 : totals.available_balance.toLocaleString("en-IN")}
                  </p>
                </td>
              </>
            )}
            <td />
            <td />
            <td />
            <td />
            <td />
            <td>
              <div role="group" className="btn-group" />
            </td>
          </tr>
          {sortedList.map((row) => (
            <tr key={row.randomId} role="row">
              <td>
                <UserNameCol rowData={row} isListClientsPath={isListClientsPath} />
              </td>
              <td>
                <p className="text-right mb-0 cp">
                  {row.credit_reference?.toLocaleString("en-IN")}
                </p>
              </td>
              {isListClients && (
                <>
                  <td>
                    <p className="text-right mb-0">
                      {row.balance?.toLocaleString("en-IN")}
                    </p>
                  </td>
                  <td>
                    <p className="text-right mb-0">
                      {row.client_pnl?.toLocaleString("en-IN")}
                    </p>
                  </td>
                  <td className="text-right">
                    <p className="mb-0 text-right">
                      {row.user_exposure?.toLocaleString("en-IN")}
                    </p>
                  </td>
                  <td>
                    <p className="text-right mb-0">
                      {row.available_balance?.toLocaleString("en-IN")}
                    </p>
                  </td>
                </>
              )}
              <td>
                <div className="mb-1 custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    disabled
                    className="custom-control-input"
                    checked={row.user_status}
                    readOnly
                    id={`ust-${row.randomId}`}
                  />
                  <label className="custom-control-label" htmlFor={`ust-${row.randomId}`} aria-label="User Status" />
                </div>
              </td>
              <td>
                <div className="mb-1 custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    disabled
                    className="custom-control-input"
                    checked={row.bet_status}
                    readOnly
                    id={`bst-${row.randomId}`}
                  />
                  <label className="custom-control-label" htmlFor={`bst-${row.randomId}`} aria-label="Bet Status" />
                </div>
              </td>
              <td>{row.exposure_limit}</td>
              <td>
                <p className="text-left mb-0">{row.default}</p>
              </td>
              <td>{row.account_type}</td>
              <td>
                <UserRowActionButtons rowData={row} parentUserId={parentUserId} isListClients={isListClients} />
              </td>
            </tr>
          ))}
          {filteredAccountList.length === 0 && (
            <tr>
              <td colSpan={colSpan} className="text-center">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;

// ---------------------------------------------------------------------------

const UserNameCol: React.FC<{ rowData: ITemp; isListClientsPath: boolean }> = ({
  rowData,
  isListClientsPath,
}) => {
  const isUser = rowData.account_type?.toLowerCase() === "user";

  if (isUser) {
    return (
      <span title={rowData.display_name ?? rowData.username} className="wrape-text">
        {rowData.username}
      </span>
    );
  }

  return (
    <Link
      target="_blank"
      to={`/${isListClientsPath ? "list-clients" : "active-users"}/${rowData.id}`}
      className="wrape-text"
      title={rowData.display_name ?? rowData.username}
    >
      <span>{rowData.username}</span>
    </Link>
  );
};
